import React, { useMemo } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import _, { clone, get, set, isEmpty } from "lodash";
import { faker } from "@faker-js/faker";
import {
    useCreateDashboardStore,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from "..";
import DashboardCreateSelectSensorModal from "../modal/SelectDevices";
import CreateChartModal from "../modal/CreateChart";
import {
    BULK_CREATE_WIDGET_MESSAGE,
    COLORS,
    MultipleSensorChartType,
    SensorChartTypeEnum,
} from "constant";
import { getUnitSymbolArray, parseColorToHex } from "utils/functions";

import { BlockKeyProps } from "types/Charts";
import { MultipleChartBlock } from "../MultipleChartBlock";
import { ColorPickerBackground } from "components/common";
import CreatePeopleHeatmapCharts from "../CreatePeopleHeatmapCharts";
import CreateCombinedSensorsChart from "../CreateCombinedSensorsChart";
import CreateCardMultiple from "./CreateCardMultiple";
import "./styles.css";
import { ChartSource, ChartType } from "constant/chart";
import SingleSelectedDeviceList from "./SingleSelectedDeviceList";
import { WidgetTypeList, DeviceSelected } from "types";
import DialGrade from "./CreateDialGrade";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";
import { getChartSourceFromWidgetType, getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const maxDevicesAllowed = 8;

export const SingleSensorCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "sensorName",
            name: "Sensor",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: LDSU_UUID_LABEL,
            className: "flex-basis-220px",
            render: (data: any) => {
                return <div className="text-nowrap text-white">{data.ldsuUID}</div>;
            },
        },
        {
            key: "unit",
            name: "Unit",
            className: "flex-basis-120px",
        },
        {
            key: "color",
            name: "Color",
            render: (data: any) => (
                <div className="color-picker__toggle">
                    <ColorPickerBackground
                        $inputColor={parseColorToHex(data.color)}
                    />
                </div>
            ),
        },
        {
            key: "alert",
            name: "Alert line",
            render: (data: any) => (
                <Form.Check
                    custom
                    type="switch"
                    checked={data.alert}
                    disabled
                />
            ),
        },
    ],
    transform: (data: any) => ({
        attributes: [
            {
                color: data.color,
                device_id: data.ldsuUID,
                gateway_id: data.gatewayId,
                name: data.sensorType,
                said: data.said,
                show_alert: data.alert || false,
                time_series: getMQTTQueryByDevice(data),
                unit: data.unit || "na",
            },
        ],
        source: ChartSource.SINGLE_SENSOR,
        name: data.chartName || "Single Chart",
        chart: ChartType.SINGLE_LINE,
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.device_id}.chartName"]`,
            isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const SingleDialCreateData: CreateChartState = {
    ...SingleSensorCreateData,
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Dial Name",
        },
        {
            key: "sensorName",
            name: "Sensor",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: LDSU_UUID_LABEL,
            className: "flex-basis-220px",
            render: (data: any) => (
                <div className="text-nowrap text-white">{data.ldsuUID}</div>
            ),
        },
        {
            key: "unit",
            name: "Unit",
            className: "flex-basis-120px",
        },
        {
            key: "color",
            name: "Color",
            render: (data: any) => (
                <div className="color-picker__toggle">
                    <ColorPickerBackground
                        $inputColor={parseColorToHex(data.color)}
                    />
                </div>
            ),
        },
    ],
    transform: (data: DeviceSelected, widgetType: WidgetType) => {
        const { source, chart } = getChartSourceFromWidgetType(widgetType);
        return {
            attributes: [
                {
                    color: data.color,
                    device_id: data.ldsuUID,
                    gateway_id: data.gatewayId,
                    name: data.sensorType,
                    said: data.said,
                    show_alert: data.alert || false,
                    time_series: getMQTTQueryByDevice(data),
                    unit: data.unit,
                },
            ],
            source: source,
            name: data.chartName,
            chart: chart,
        };
    },
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.device_id}.chartName"]`,
            isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const SingleCardCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "sensorName",
            name: "Sensor",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: LDSU_UUID_LABEL,
            className: "flex-basis-220px",
            render: (data: any) => (
                <div className="text-nowrap text-white text-truncate">
                    {data.ldsuUID}
                </div>
            ),
        },
    ],
    transform: (data, widgetType: WidgetType) => {
        const { source, chart } = getChartSourceFromWidgetType(widgetType);
        return {
            attributes: [
                {
                    device_id: data.ldsuUID,
                    gateway_id: data.gatewayId,
                    name: data.sensorType,
                    said: data.said,
                    time_series: getMQTTQueryByDevice(data),
                    unit: data.unit ?? "na",
                    show_alert: false,
                },
            ],
            source: source,
            name: data.chartName,
            chart: chart,
        };
    },
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.deviceId}.chartName"]`,
            isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const MultipleSensorCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: true,
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Widget Name",
        },
        {
            key: "sensorType",
            name: "Sensor Type",
        },
        {
            key: "sensors_count",
            name: "No. of Sensors",
            render: (data: any) => data.attributes?.length,
        },
        {
            key: "unit",
            name: "Unit",
            render: (data: any) => {
                const arrayOfUnits = data.unit
                    .split(",")
                    .reduce((acc: any, symbol: any) => {
                        if (symbol.trim()) acc.push(symbol);
                        return acc;
                    }, []);

                return arrayOfUnits.join(", ");
            },
        },
    ],
    transform: (data: any) => ({
        attributes: data.attributes.map((d: any) => ({
            color: d.color,
            device_id: d.ldsuUID,
            gateway_id: d.gatewayId,
            name: d.sensorType,
            said: String(d.said),
            show_alert: false,
            time_series: getMQTTQueryByDevice(d),
            unit: d.unit,
        })),
        source: ChartSource.MULTIPLE_SENSORS,
        chart: ChartType.MULTIPLE_LINES,
        name: data.chart_name || "Multiple Sensor Chart",
    }),
    validate() {
        return this.data.map((item) => ({
            path: "",
            isValid: CHART_NAME_PATTERN.test(item.chart_name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const MultipleHeatmapCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: false,
    data: [],
    columns: [
        {
            key: "name",
            name: "Widget Name",
        },
        {
            key: "type",
            name: "Sensor Type",
        },
        {
            key: "sensors_count",
            name: "No. of Sensors",
            render: (data: any) => data.attributes?.length,
        },
    ],
    transform: (data: any) => {
        return {
            attributes: data.attributes.map((d: any) => ({
                gateway_id: d.gatewayId,
                device_id: d.ldsuUID,
                name: d.sensorType,
                said: String(d.said),
            })),
            source: ChartSource.MULTIPLE_SENSORS,
            chart: ChartType.HEATMAP,
            name: data.name || "Heatmap Chart",
        };
    },
    validate() {
        const validates = this.data.map((item) => ({
            path: "",
            isValid: CHART_NAME_PATTERN.test(item.name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        const selectedSensors = (this.data[0].attributes || []).filter(
            (attr: any) => attr.deviceId
        );
        if (selectedSensors.length !== 4) {
            validates.push({
                path: "",
                isValid: false,
                message: "Please select 4 sensors to create a combined chart.",
            });
        }

        return validates;
    },
};

export const SingleHeatmapCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: false,
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "sensorName",
            name: "Sensor",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: "UUID",
        },
    ],
    transform: (data: any) => {
        return {
            attributes: data.attributes.map((d: any) => ({
                gateway_id: d.gatewayId,
                device_id: d.ldsuUID,
                name: d.sensorType,
                said: String(d.said),
            })),
            source: ChartSource.SINGLE_SENSOR,
            chart: ChartType.HEATMAP,
            name: data.chartName || "Heatmap Chart",
        };
    },
    validate() {
        return this.data.map((item) => ({
            path: "",
            isValid:
                item.chartName &&
                CHART_NAME_PATTERN.test(item.chartName?.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const CombinedSensorCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: false,
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Chart Name",
        },
        {
            key: "sensorType",
            name: "Sensor Type",
        },
        {
            key: "sensorsCount",
            name: "No. of Sensors",
            render: (data: any) => data.attributes?.length,
        },
        {
            key: "color",
            name: "Color",
            render: (data: any) => (
                <div className="color-picker__toggle">
                    <ColorPickerBackground
                        $inputColor={parseColorToHex(data.color)}
                    />
                </div>
            ),
        },
    ],
    transform: (data: any, chartType) => ({
        attributes: data.attributes.map((d: any) => ({
            color: d.color,
            device_id: d.ldsuUID,
            gateway_id: d.gatewayId,
            name: d.sensorType,
            said: String(d.said),
            show_alert: false,
            time_series: getMQTTQueryByDevice(d),
            unit: "na",
        })),
        source: ChartSource.MULTIPLE_SENSORS,
        chart: "SINGLE_LINE",
        name: data.chartName || "Combined Chart",
    }),
    validate() {
        const validates = this.data.map((item) => ({
            path: "",
            isValid:
                item.chartName &&
                CHART_NAME_PATTERN.test(item.chartName?.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        if (this.data[0].attributes.length < 2) {
            validates.push({
                path: "",
                isValid: false,
                message:
                    "Please select at least 2 sensors to create a combined chart.",
            });
        }

        return validates;
    },
};

const WidgetDataTemplateMap: Record<string, CreateChartState> = {
    [WidgetTypeList.Chart]: SingleSensorCreateData,
    [WidgetTypeList.Card]: SingleCardCreateData,
    [WidgetTypeList.Dial]: SingleDialCreateData,
};

export const getSensorChartType = (widgetType: WidgetType) => {

    switch (widgetType) {
        case WidgetType.SENSOR_CHART_MULTIPLE:
            return SensorChartTypeEnum.multiple;
        case WidgetType.SENSOR_CHART_AGGREGATE:
            return SensorChartTypeEnum.combinedSensors;
        case WidgetType.SENSOR_PCS_HEATMAP:
        case WidgetType.SENSOR_PCS_COMBINED_HEATMAP:
            return SensorChartTypeEnum.peopleHeatmap;
        default: return SensorChartTypeEnum.single;
    }
};

const DashboardCreateSensorComponent: React.FunctionComponent = () => {
    const sensorType = "People Count Sensor";
    const [showSelectModal, setShowSelectModal] = React.useState(false);
    const [singleSelectedDevices, setSingleSelectedDevices] = React.useState<{
        [key: string]: DeviceSelected;
    }>({});
    const { createModalShow, setCreateModalShow, createData, widget, setWidget } =
        useCreateDashboardStore();

    const [multipleChartList, setMultipleChartList] = React.useState<
        BlockKeyProps[]
    >([]);
    const [multipleChartData, setMultipleChartData] = React.useState<object>(
        {}
    );
    const [heatmapChartData, setHeatmapChartData] = React.useState<any>({});
    const [combinedChartData, setCombinedChartData] = React.useState<any>([]);

    const selectedDevices = Object.values(singleSelectedDevices);


    const widgetTypeForConfig =
        widget?.widgetType ?? WidgetType.SENSOR_CHART_SINGLE;
    const widgetConfig = useMemo(
        () => getWidgetConfigByWidgetType(widgetTypeForConfig),
        [widgetTypeForConfig]
    );

    const widgetType = widget?.type ?? WidgetTypeList.Chart;
    const sensorChartType = getSensorChartType(widgetTypeForConfig);

    const filterDevice = useMemo(
        () => widgetConfig?.filter?.(widgetTypeForConfig),
        [widgetConfig, widgetTypeForConfig]
    );

    React.useEffect(() => {
        const currentWidgetType = widget?.widgetType;
        const isPeopleCountHeatmapWidget =
            currentWidgetType === WidgetType.SENSOR_PCS_HEATMAP ||
            currentWidgetType === WidgetType.SENSOR_PCS_COMBINED_HEATMAP;

        if (isPeopleCountHeatmapWidget) {
            let nextWidgetType: WidgetType | undefined;
            if (heatmapChartData.displayType === 1) {
                nextWidgetType = WidgetType.SENSOR_PCS_COMBINED_HEATMAP;
            } else if (heatmapChartData.displayType === 2) {
                nextWidgetType = WidgetType.SENSOR_PCS_HEATMAP;
            }

            if (
                typeof nextWidgetType === "number" &&
                currentWidgetType !== nextWidgetType &&
                widget
            ) {
                setWidget({
                    ...widget,
                    widgetType: nextWidgetType,
                });
            }
        }

        if (heatmapChartData.displayType === 1) {
            const createData = [
                {
                    name: heatmapChartData.chartName,
                    attributes: heatmapChartData.combinedSensorList,
                    type: sensorType,
                    uploadImage: heatmapChartData.uploadImage,
                },
            ];

            useCreateDashboardStore.setState({
                createData: {
                    ...MultipleHeatmapCreateData,
                    data: createData,
                },
            });
        } else if (heatmapChartData.displayType === 2) {
            const createData = (heatmapChartData.singleChartList || []).map(
                (item: any) => ({
                    ...item,
                    chartName: item.chartName || item.sensorName,
                    attributes: [{ ...item }],
                })
            );

            useCreateDashboardStore.setState({
                createData: {
                    ...SingleHeatmapCreateData,
                    data: createData,
                },
            });
        }
    }, [heatmapChartData, setWidget, widget]);

    React.useEffect(() => {
        const createData = [
            {
                chartName: combinedChartData.chartName,
                attributes: (combinedChartData.sensorList || []).map(
                    (sensor: any) => ({
                        ...sensor,
                        color: combinedChartData.chartColor,
                    })
                ),
                color: combinedChartData.chartColor,
                sensorType: get(
                    combinedChartData,
                    "sensorList[0].sensorType",
                    sensorType
                ),
            },
        ];

        useCreateDashboardStore.setState({
            createData: {
                ...CombinedSensorCreateData,
                data: createData,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [combinedChartData, widgetConfig]);

    React.useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...WidgetDataTemplateMap[widgetType],
                data: Object.values(singleSelectedDevices),
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleSelectedDevices]);

    React.useEffect(() => {
        const createData: any[] = [];

        for (let blockData of Object.values(multipleChartData)) {
            const chart_name = blockData.chart_name;
            const chartDevices: any[] = [];
            const multipleChartType = blockData.multipleChartType as keyof typeof MultipleSensorChartType;

            if (MultipleSensorChartType[multipleChartType] === MultipleSensorChartType.same) {
                const devices = blockData.data["0"]?.devices;
                if (devices) {
                    chartDevices.push(...Object.values(devices));
                }
            } else {
                Object.values(blockData.data).forEach((column: any) => {
                    if (column?.devices) {
                        chartDevices.push(...Object.values(column.devices));
                    }
                });
            }

            const types = Object.keys(chartDevices.pluck("sensorType")).join(
                " x "
            );
            const units = Object.keys(chartDevices.pluck("unit")).join(", ");

            !!chartDevices.length &&
                createData.push({
                    chart_name,
                    sensorType: types,
                    unit: units,
                    attributes: chartDevices,
                });
        }

        useCreateDashboardStore.setState({
            createData: {
                ...MultipleSensorCreateData,
                data: createData,
            },
        });
    }, [multipleChartData, widgetConfig]);



    const handleUnitOptions = (device: any) => {
        if (device.APP) {
            if (device.unit === "BINARY") return [""];

            const appSensor = device.APP.find(
                (d: any) => d.APPID === device.APPID
            );
            return getUnitSymbolArray(appSensor);
        } else {
            return device.units || (device.unit ?? "").split(",");
        }
    };

    const onRemove = (device: DeviceSelected) => {
        setSingleSelectedDevices((prev: any) => _.omit(prev, device.deviceId));
    };

    const onChange = (
        device: DeviceSelected,
        changes: Partial<DeviceSelected>
    ) => {
        const devices_ = clone(singleSelectedDevices);
        const deviceChanges_ = { ...device, ...changes };
        set(devices_, device.deviceId, deviceChanges_);
        setSingleSelectedDevices(devices_);
    };



    const handleOnDelete = (deleteItem: BlockKeyProps) => {
        setMultipleChartData((prev) => _.omit(prev, deleteItem.key));

        setMultipleChartList((prev) =>
            prev.filter((item) => item.key !== deleteItem.key)
        );
    };

    const createDebouncedHandler = (itemKey: string) => {
        return _.debounce((data: any) => {
            setMultipleChartData((prev: any) => ({
                ...prev,
                [itemKey]: data,
            }));
        }, 100);
    };

    const mapSelectedDevicesForSave = (
        data: Record<string, DeviceSelected>,
        prev: Record<string, DeviceSelected>
    ) =>
        Object.values(data)
            .map((item: DeviceSelected, index: number) => {
                const prevItem = prev[item.deviceId];
                return {
                    ...item,
                    chartName: prevItem?.chartName ?? item.sensorName,
                    color: prevItem?.color ?? COLORS[index] ?? faker.color.rgb(),
                    alert: false,
                    unitOptions: handleUnitOptions(item),
                };
            })
            .pluck("deviceId");

    const renderSingle = () => {
        return (
            <>
                <Row>
                    <Col sm="12">
                        <Button
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        <p className="reusable-description mt-2">
                            {BULK_CREATE_WIDGET_MESSAGE.fill({
                                type: "sensors",
                            })}
                        </p>
                        <DashboardCreateSelectSensorModal
                            show={showSelectModal}
                            onHide={() => setShowSelectModal(false)}
                            values={singleSelectedDevices}
                            onSave={(_event, data) =>
                                setSingleSelectedDevices((prev) =>
                                    mapSelectedDevicesForSave(data, prev)
                                )
                            }
                            deviceType="Sensor"
                            filter={filterDevice}
                        />
                    </Col>
                </Row>
                <Row>
                    {selectedDevices.length > 0 && (
                        <SingleSelectedDeviceList
                            devices={selectedDevices}
                            type={widgetType}
                            onRemove={onRemove}
                            onChange={onChange}
                        />
                    )}
                </Row>
            </>
        );
    };

    const renderMultiple = () => {
        return (
            <>
                <p className="sub-text">
                    {`In a Chart - Multi-sensor comparison, you can add max {maxDevicesAllowed} sensors (Sensors with same sensor type or two different sensor types. i.e. 2 y-axis can plot with different units)`.fill(
                        {
                            maxDevicesAllowed: String(maxDevicesAllowed),
                        }
                    )}
                </p>
                {multipleChartList.map((item) => (
                    <React.Fragment key={item.key}>
                        <MultipleChartBlock
                            blockKey={item}
                            onDelete={handleOnDelete}
                            onChange={createDebouncedHandler(item.key)}
                        />
                    </React.Fragment>
                ))}

                <Button
                    className="mt-3"
                    variant="primary"
                    onClick={() =>
                        setMultipleChartList((prev) => [
                            ...prev,
                            {
                                key: getId(),
                            },
                        ])
                    }
                >
                    + Create Multiple Sensor Chart
                </Button>
                {!isEmpty(multipleChartList) && (
                    <p className="sub-text mt-2">
                        Continue creating another Chart - Multi-sensor
                        comparison. They'll all be saved together in a single
                        step.
                    </p>
                )}
            </>
        );
    };



    const renderWidget = () => {
        if (widget?.widgetType === WidgetType.SENSOR_DIAL_READING_GRADE) {
            return <DialGrade />;
        }

        if (widget?.widgetType === WidgetType.SENSOR_CARD_MULTI_VALUES) {
            return <CreateCardMultiple widget={widget} />;
        }

        if (sensorChartType === SensorChartTypeEnum.single) {
            return renderSingle();
        }

        if (sensorChartType === SensorChartTypeEnum.multiple) {
            return renderMultiple();
        }

        if (sensorChartType === SensorChartTypeEnum.peopleHeatmap) {
            return <CreatePeopleHeatmapCharts
                onChange={(data: any) => setHeatmapChartData(data)}
            />;
        }

        if (sensorChartType === SensorChartTypeEnum.combinedSensors) {
            return <CreateCombinedSensorsChart
                onChange={(data: any) => setCombinedChartData(data)}
            />;
        }

        return null;
    };

    return (
        <React.Fragment>

            {renderWidget()}

            <CreateChartModal
                show={createModalShow && !_.isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(DashboardCreateSensorComponent);
